/**
 * Web mirror of packages/domain/src/gateCredentials.ts.
 * CRA cannot transpile workspace TypeScript packages directly.
 * Keep in sync with @bilt/domain (mobile uses the package).
 */

export const GATE_TICKET_SCHEME = 'bilt://t/';
export const GATE_ENTRY_SCHEME = 'bilt://e/';

/** Encode a spectator ticket public_token for QR display. */
export function encodeSpectatorTicketPayload(publicToken: string): string {
  const token = publicToken.trim();
  if (!token) return '';
  if (/^bilt:\/\/[te]\//i.test(token)) return token;
  return `${GATE_TICKET_SCHEME}${token}`;
}

/** Encode a vehicle registration entry token for QR display. */
export function encodeVehicleEntryPayload(entryToken: string): string {
  const token = entryToken.trim();
  if (!token) return '';
  if (/^bilt:\/\/[te]\//i.test(token)) return token;
  return `${GATE_ENTRY_SCHEME}${token}`;
}

export type GatePayloadKind = 'spectator_ticket' | 'vehicle_entry' | 'raw' | 'passport_or_build' | 'empty';

export type NormalizedGatePayload = {
  kind: GatePayloadKind;
  /** Opaque token to send to event_gate_scan (never a Passport URL). */
  token: string;
  /** Human-readable rejection when kind is passport_or_build. */
  rejectReason?: string;
};

function isPassportOrBuildPayload(input: string): boolean {
  if (/bilt:\/\/p\//i.test(input)) return true;
  if (/\/passport\b/i.test(input)) return true;
  if (/passport\/s\//i.test(input)) return true;
  if (!/^https?:\/\//i.test(input)) return false;
  if (/\/t\//i.test(input) || /\/e\//i.test(input) || /bilt:\/\/[te]\//i.test(input)) {
    return false;
  }
  return /biltspec\.com\//i.test(input) || /localhost(:\d+)?\//i.test(input);
}

/**
 * Normalize a scanned or typed gate payload into an opaque token.
 * Rejects Passport / public build URLs so they are never admitted at the gate.
 */
export function normalizeGateScanPayload(raw: string): NormalizedGatePayload {
  const input = (raw || '').trim();
  if (!input) {
    return { kind: 'empty', token: '', rejectReason: 'Empty code' };
  }

  if (isPassportOrBuildPayload(input)) {
    return {
      kind: 'passport_or_build',
      token: '',
      rejectReason: 'This is a build Passport QR, not an entry ticket.',
    };
  }

  if (input.toLowerCase().startsWith(GATE_TICKET_SCHEME)) {
    return { kind: 'spectator_ticket', token: input.slice(GATE_TICKET_SCHEME.length).trim() };
  }
  if (input.toLowerCase().startsWith(GATE_ENTRY_SCHEME)) {
    return { kind: 'vehicle_entry', token: input.slice(GATE_ENTRY_SCHEME.length).trim() };
  }

  const httpsTicket = input.match(/^https?:\/\/[^/]+\/t\/([^/?#]+)/i);
  if (httpsTicket?.[1]) {
    return { kind: 'spectator_ticket', token: decodeURIComponent(httpsTicket[1]) };
  }
  const httpsEntry = input.match(/^https?:\/\/[^/]+\/e\/([^/?#]+)/i);
  if (httpsEntry?.[1]) {
    return { kind: 'vehicle_entry', token: decodeURIComponent(httpsEntry[1]) };
  }

  const pathTicket = input.match(/^\/t\/([^/?#]+)/i);
  if (pathTicket?.[1]) {
    return { kind: 'spectator_ticket', token: decodeURIComponent(pathTicket[1]) };
  }
  const pathEntry = input.match(/^\/e\/([^/?#]+)/i);
  if (pathEntry?.[1]) {
    return { kind: 'vehicle_entry', token: decodeURIComponent(pathEntry[1]) };
  }

  return { kind: 'raw', token: input };
}
